import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { AdAccountGroupAdAccountModel, ExternalAdAccountModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/adAccountGroupAdAccount/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/adAccountGroupAdAccount/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/adAccountGroupAdAccount/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/adAccountGroupAdAccount/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/adAccountGroupAdAccount/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/adAccountGroupAdAccount/CREATE_FAILURE'

const DELETE_REQUEST = 'realbase/adAccountGroupAdAccount/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/adAccountGroupAdAccount/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/adAccountGroupAdAccount/DELETE_FAILURE'

export type AdAccountGroupAdAccountModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
}

// Initial State
const initialState: AdAccountGroupAdAccountModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAdAccountGroupAdAccounts(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().adAccountGroupAdAccounts.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/ad-account-group-ad-accounts', options)
      .then(({ data }: { data: { data: AdAccountGroupAdAccountModel[] } }) => {
        const normalizedJson = normalize(data.data, Schemas.AD_ACCOUNT_GROUP_AD_ACCOUNT_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data: data.data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export type CreateAndLinkAdAccountsParams = {
  adAccountGroupId: number,
  externalAdAccounts: ExternalAdAccountModel[],
}

export function createAndLinkAdAccounts(params: CreateAndLinkAdAccountsParams) {
  const config = {
    method: 'POST',
    data: JSON.stringify(params),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/ad-account-group-ad-accounts/create-and-link-multiple', {}, config)
      .then(({ data }: { data: AdAccountGroupAdAccountModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AD_ACCOUNT_GROUP_AD_ACCOUNT_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }  
}

export function deleteAdAccountGroupAdAccount(adAccountGroupAdAccount: DeleteParams<AdAccountGroupAdAccountModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/ad-account-group-ad-accounts/${adAccountGroupAdAccount.id}`, {}, config)
      .then(({ status }) => {
        if (status !== 200) throw new Error()

        const deletedEntity = { id: adAccountGroupAdAccount.id }
        const normalizedJson = normalize(deletedEntity, Schemas.AD_ACCOUNT_GROUP_AD_ACCOUNT)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data: deletedEntity }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: AdAccountGroupAdAccountModuleState = initialState,
  action: Action = { type: '' },
): AdAccountGroupAdAccountModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
